import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { linko } from "@shared/linko";
import type { Opportunity } from "@entities";
import {
  buildWhatsappRegistrationLink,
  formatWhatsappWhen,
  getWhatsappTemplate,
  listWhatsappTemplatesForContext,
  renderWhatsappMessage,
  whatsappFirstName,
  WHATSAPP_PLACEHOLDER_LABELS,
  type WhatsappPlaceholderValues,
  type WhatsappTemplateDefinition,
} from "@config/whatsapp-templates.config";

export type WhatsappDialogRecipient = {
  userId: string;
  name?: string | null;
  firstName?: string | null;
  phoneNumber?: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  sending: boolean;
  recipientCount: number;
  recipients?: WhatsappDialogRecipient[];
  /** Shown in the intro. Approved participants, ranked volunteers, or the volunteers directory. */
  audience?: "approved" | "relevant" | "directory";
  sampleUserName?: string;
  opportunityId?: string;
  opportunity?: Pick<
    Opportunity,
    "id" | "title" | "startDate" | "location" | "timeDescription" | "hasTime"
  >;
  onSend: (
    templateKey: string,
    customFields: Record<string, string> | undefined,
    confirmedRecentUserIds: string[],
  ) => Promise<void>;
};

export function SendEventParticipantsWhatsappDialog({
  open,
  onClose,
  sending,
  recipientCount,
  recipients,
  audience = "approved",
  sampleUserName,
  opportunityId,
  opportunity,
  onSend,
}: Props) {
  const templates = useMemo(
    () => listWhatsappTemplatesForContext("event_participants"),
    [],
  );
  const [templateKey, setTemplateKey] = useState(
    () => templates[0]?.key ?? "",
  );
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [checkedRecent, setCheckedRecent] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    setEdits({});
  }, [open, templateKey]);

  useEffect(() => {
    if (!open) return;
    setCheckedRecent(new Set());
  }, [open]);

  const template: WhatsappTemplateDefinition | undefined = getWhatsappTemplate(
    templateKey,
  );
  const shownRecipients = recipients ?? [];
  const recipientIds = shownRecipients.map((recipient) => recipient.userId);
  const recentQuery = useQuery({
    queryKey: ["whatsapp-recent-sends", recipientIds.join(",")],
    enabled: open && recipientIds.length > 0,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data } = await linko.opportunity.queries.getWhatsappRecentSends({
        userIds: recipientIds,
      });
      return data;
    },
  });
  const recentReady =
    shownRecipients.length === 0 ||
    (recentQuery.isSuccess && !recentQuery.isFetching);
  const recentByUserId = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of recentQuery.data ?? []) {
      map.set(row.userId, row.lastSentAt);
    }
    return map;
  }, [recentQuery.data]);
  const willSend = shownRecipients.filter((recipient) => {
    if (!recentByUserId.has(recipient.userId)) return true;
    return checkedRecent.has(recipient.userId);
  });
  const count =
    shownRecipients.length > 0 ? willSend.length : recipientCount;
  const uncheckedRecent = shownRecipients.filter(
    (recipient) =>
      recentByUserId.has(recipient.userId) &&
      !checkedRecent.has(recipient.userId),
  );
  const sampleRecipient = willSend[0] ?? shownRecipients[0];
  const sampleFirstName =
    (sampleRecipient ? whatsappFirstName(sampleRecipient) : "") ||
    whatsappFirstName({ name: sampleUserName }) ||
    "מתנדב/ת";
  const autoName = sampleFirstName;
  const taskId = opportunity?.id || opportunityId || "";

  const autoValues: WhatsappPlaceholderValues = {
    user_name: autoName,
    request_title: opportunity ? opportunity.title || "משימה" : "",
    when: opportunity
      ? formatWhatsappWhen({
          startDate: opportunity.startDate,
          timeDescription: opportunity.timeDescription,
          hasTime: opportunity.hasTime,
        })
      : "",
    location: opportunity?.location || "",
    registration_link: taskId ? buildWhatsappRegistrationLink(taskId) : "",
    sender_name: "מערכת Weme",
  };

  const fieldValue = (id: string) =>
    id in edits ? edits[id] : (autoValues[id as keyof WhatsappPlaceholderValues] ?? "");

  const nameEdited = fieldValue("user_name").trim() !== autoName.trim();

  const canSend =
    !!template &&
    recentReady &&
    count > 0 &&
    (template?.bodyPlaceholders ?? []).every((id) => {
      if (id === "user_name") return true;
      return fieldValue(id).trim().length > 0;
    });

  const previewValues: WhatsappPlaceholderValues = {};
  for (const id of template?.bodyPlaceholders ?? []) {
    previewValues[id] = fieldValue(id);
  }

  const fieldsToSend = () => {
    const customFields: Record<string, string> = {};
    for (const id of template?.bodyPlaceholders ?? []) {
      const value = fieldValue(id).trim();
      if (id === "user_name" && (!nameEdited || !value)) continue;
      if (!value) continue;
      customFields[id] = value;
    }
    return customFields;
  };

  const nameNote =
    count > 1
      ? nameEdited
        ? "כל הנמענים יקבלו את השם שהוקלד."
        : "כל נמען יקבל את השם הפרטי שלו. התצוגה משתמשת בשם של הראשון ברשימה."
      : undefined;

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>שליחת וואטסאפ</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {recentQuery.isFetching
              ? "בודקים מי קיבל וואטסאפ בשבוע האחרון."
              : recentQuery.isError
                ? "לא הצלחנו לבדוק שליחות אחרונות. אי אפשר לשלוח לפני שהבדיקה מצליחה."
                : count === 0 && uncheckedRecent.length > 0
                  ? `אף אחד לא יישלח. ${uncheckedRecent.length} קיבלו וואטסאפ בשבוע האחרון. סמנו את מי שכן לשלוח אליו.`
                : `יישלח ל-${count} ${
                    audience === "relevant"
                      ? "מתנדבים רלוונטיים"
                      : audience === "directory"
                        ? "מתנדבים"
                        : "מתנדבים מאושרים"
                  }. השדות מולאו אוטומטית ואפשר לערוך אותם.${
                    uncheckedRecent.length > 0
                      ? ` עוד ${uncheckedRecent.length} קיבלו וואטסאפ בשבוע האחרון ולא יישלחו אלא אם תסמנו אותם.`
                      : ""
                  }`}
          </Typography>
          {recentQuery.isError ? (
            <Button onClick={() => void recentQuery.refetch()} disabled={sending}>
              נסו שוב
            </Button>
          ) : null}
          {recentReady && shownRecipients.length > 0 && (
            <Box
              sx={{
                maxHeight: 280,
                overflow: "auto",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              {shownRecipients.map((recipient) => {
                const lastSentAt = recentByUserId.get(recipient.userId);
                const name = whatsappFirstName(recipient) || "בלי שם";
                const phone = recipient.phoneNumber
                  ? ` · ${recipient.phoneNumber}`
                  : " · בלי טלפון";
                if (!lastSentAt) {
                  return (
                    <Typography key={recipient.userId} variant="body2">
                      {name}
                      {phone}
                    </Typography>
                  );
                }
                const sentLabel = new Date(lastSentAt).toLocaleDateString("he-IL");
                return (
                  <FormControlLabel
                    key={recipient.userId}
                    sx={{ display: "flex", alignItems: "flex-start", mx: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={checkedRecent.has(recipient.userId)}
                        disabled={sending}
                        onChange={(_, checked) => {
                          setCheckedRecent((current) => {
                            const next = new Set(current);
                            if (checked) next.add(recipient.userId);
                            else next.delete(recipient.userId);
                            return next;
                          });
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {name}
                        {phone}
                        {` · קיבל וואטסאפ ב-${sentLabel}. סמנו כדי לשלוח שוב.`}
                      </Typography>
                    }
                  />
                );
              })}
            </Box>
          )}
          <FormControl fullWidth>
            <InputLabel id="whatsapp-template-label">טמפלייט</InputLabel>
            <Select
              labelId="whatsapp-template-label"
              label="טמפלייט"
              value={templateKey}
              onChange={(e) => setTemplateKey(String(e.target.value))}
              disabled={sending}
            >
              {templates.map((item) => (
                <MenuItem key={item.key} value={item.key}>
                  {item.labelHe}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {template?.bodyPlaceholders.map((id) => (
            <TextField
              key={id}
              label={WHATSAPP_PLACEHOLDER_LABELS[id]}
              value={fieldValue(id)}
              onChange={(e) =>
                setEdits((current) => ({ ...current, [id]: e.target.value }))
              }
              helperText={id === "user_name" ? nameNote : undefined}
              multiline={id === "custom_message"}
              minRows={id === "custom_message" ? 3 : undefined}
              disabled={sending}
              fullWidth
            />
          ))}
          {template && (
            <Box
              sx={{
                bgcolor: "action.hover",
                borderRadius: 1,
                px: 1.5,
                py: 1.5,
                whiteSpace: "pre-wrap",
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                תצוגה מקדימה
              </Typography>
              <Typography variant="body2">
                {renderWhatsappMessage(template, previewValues)}
              </Typography>
              {nameNote && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {nameNote}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          ביטול
        </Button>
        <Button
          variant="contained"
          disabled={!canSend || sending}
          onClick={() =>
            onSend(templateKey, fieldsToSend(), [...checkedRecent])
          }
        >
          {sending ? "שולח..." : "שליחה"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
